import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { DateTime } from 'effect';
import { Effect, Request, RequestResolver } from 'effect';

import type {
  ApiInfrastructureErrors,
  BackendApiErrorUnion,
} from '../../../data/error/backendApi/_internal/createBackendApiError';
import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { liftRequest } from '../context';
import { BrandExpandedResponse } from '@vs-shop/schemas/brand/brand';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import { BackendApi } from '../../../service/api/BackendApi';
import type { AuthToken } from '../../../service/auth/AuthToken';
import type { LoggerConfig } from '../../../service/logger/LoggerConfig';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import type * as HttpClient from 'effect/unstable/http/HttpClient';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/brands/{id}/expand';
const HTTP_METHOD = 'get';

export const GetBrandExpandedByIdError = createBackendApiError({
  domain: 'Brand',
  endpoint: 'GetBrandExpandedById',
  path: ENDPOINT,
  identifierField: 'id',
} as const);

export type BrandGetBrandExpandedByIdError = BackendApiErrorUnion<typeof GetBrandExpandedByIdError>;

const getBrandExpandedByIdFetch = (id: string) =>
  Effect.gen(function* () {
    const apiClient = yield* BackendApi;
    return yield* apiClient
      .path(ENDPOINT)
      .method(HTTP_METHOD)
      .accept('application/ld+json')
      .fetch({ params: { id: id } });
  }).pipe(
    Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
      kind: 'client',
      attributes: {
        'http.request.method': HTTP_METHOD,
        'brand.id': id,
      },
    })
  );

const getBrandExpandedById = (id: string) =>
  getBrandExpandedByIdFetch(id).pipe(
    Effect.map((response) => response.response),
    Effect.tap(addResponseStatusToSpan),
    Effect.withSpan('ValidateResponse'),
    Effect.flatMap(
      HttpClientResponse.matchStatus({
        '2xx': (response) => schemaBodyJsonClean(BrandExpandedResponse)(response),
        404: (response) =>
          Effect.fail(new GetBrandExpandedByIdError.NotFoundError({ id, response })),
        orElse: (response) =>
          Effect.fail(new GetBrandExpandedByIdError.ApiRequestError({ id, response })),
      })
    ),
    Effect.tap((result) =>
      logApiOperation(
        ApiModule.BrandGet,
        'Debug',
        `Successfully resolved brand for id: ${id} -> name: ${String(result.name)}`
      )
    ),
    Effect.withSpan('api.brand.get', {
      kind: 'internal',
      attributes: {
        'brand.id': id,
      },
    })
  );

type GetBrandExpandedByIdRequest = {
  readonly _tag: 'GetBrandExpandedByIdRequest';
  readonly id: string;
} & Request.Request<
  BrandExpandedResponse,
  BrandGetBrandExpandedByIdError | ApiInfrastructureErrors,
  BackendApi | AuthToken | HttpClient.HttpClient | LoggerConfig | DateTime.CurrentTimeZone
>;

const GetBrandExpandedByIdRequest = Request.tagged<GetBrandExpandedByIdRequest>(
  'GetBrandExpandedByIdRequest'
);

const GetBrandExpandedByIdResolver = RequestResolver.fromEffect(
  liftRequest((request: GetBrandExpandedByIdRequest) => getBrandExpandedById(request.id))
);

export const requestBrandExpandedById = (id: string) =>
  Effect.request(GetBrandExpandedByIdRequest({ id }), GetBrandExpandedByIdResolver);
